import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { usePlatformConfig } from 'contexts/PlatformConfigContext';
import { useTheme } from 'contexts/ThemeContext';

import { HelpTooltip, Text } from 'components/DataDisplay';
import Fade from 'components/Fade';
import { Item, Row } from 'components/Layout';

import { FormatInput } from './format';

export const TextField = props => {
  const {
    color,
    defaultValue,
    disabled,
    error,
    errorText,
    format,
    helperText,
    onBlur,
    onChange,
    onEnter,
    small,
    show,
    label,
    placeholder,
    type,
    unit,
    value,
    multiline,
    rows,
    bgColor,
    fontSize,
    title,
    required,
    tooltip,
    cleanOnEnter,
    inputProps: inputPropsProp
  } = props;
  const [valInput, setValInput] = React.useState(value);

  const { country } = usePlatformConfig();

  useEffect(() => {
    if (valInput !== value) {
      setValInput(value);
    }
    // eslint-disable-next-line
  }, [value]);

  const { themeColors } = useTheme();
  const {
    line,
    teal,
    tealGlow,
    ink,
    inkSoft,
    faint,
    red
  } = themeColors.designSystem;
  const colorRules = themeColors[color] || color || line;

  const useStyles = makeStyles({
    root: {
      '& .MuiInputBase-root': {
        color: ink,
        backgroundColor: bgColor || 'transparent',
        fontSize,
        borderRadius: '6px',
        minHeight: '30px'
      },
      '& .Mui-disabled': {
        backgroundColor: themeColors.tertiary
      },
      '& .MuiOutlinedInput-input': {
        padding: '5px 9px'
      },
      '& .MuiOutlinedInput-input::placeholder': {
        color: faint,
        opacity: 1
      },
      '& .MuiInputAdornment-root p': {
        color: ink,
        fontSize
      },
      '& .MuiInput-underline:after': {
        borderBottomColor: colorRules
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: colorRules
        },
        '&:hover fieldset': {
          borderColor: colorRules
        },
        '&.Mui-focused fieldset': {
          borderColor: teal
        },
        '&.Mui-focused': {
          boxShadow: `0 0 0 2px ${tealGlow}`
        }
      }
    }
  });
  const classes = useStyles();

  const validateAndFormatNumber = (inputValue, min, max) => {
    if (inputValue === '' || inputValue === null || inputValue === undefined) {
      return inputValue;
    }

    let numValue = parseFloat(inputValue);

    if (Number.isNaN(numValue)) {
      return inputValue;
    }

    if (min !== undefined && numValue < min) {
      numValue = min;
    }
    if (max !== undefined && numValue > max) {
      numValue = max;
    }

    return numValue.toString();
  };

  const handleChange = e => {
    const rawValue = format ? e.target.rawValue : e.target.value;
    let processedValue = rawValue;

    if (type === 'number' && inputPropsProp) {
      const { min, max } = inputPropsProp;
      const minNum = min !== undefined ? parseFloat(min) : undefined;
      const maxNum = max !== undefined ? parseFloat(max) : undefined;

      if (rawValue !== '' && rawValue !== null && rawValue !== undefined) {
        processedValue = validateAndFormatNumber(rawValue, minNum, maxNum);
      }
    }

    if (valInput !== processedValue) {
      onChange(processedValue);
      setValInput(processedValue);
    }
  };

  const handleBlur = e => {
    const rawValue = format ? e.target.rawValue : e.target.value;
    let processedValue = rawValue;

    if (type === 'number' && inputPropsProp) {
      const { min, max } = inputPropsProp;
      const minNum = min !== undefined ? parseFloat(min) : undefined;
      const maxNum = max !== undefined ? parseFloat(max) : undefined;

      if (rawValue !== '' && rawValue !== null && rawValue !== undefined) {
        processedValue = validateAndFormatNumber(rawValue, minNum, maxNum);
      }
    }

    onBlur(processedValue);
    setValInput(processedValue);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      onEnter(format ? e.target.rawValue : e.target.value);
      if (cleanOnEnter) {
        setValInput('');
      }
    }
  };

  return (
    <Fade show={show}>
      <FormControl
        className={classes.root}
        variant="outlined"
        margin={small ? 'dense' : 'normal'}
        size={small ? 'small' : 'medium'}
        error={error}
        style={{
          marginTop: small && '6px',
          width: '100%'
        }}
      >
        {title && (
          <Row spacing={2} justify="flex-start" alignItems="center">
            <Item
              justify="flex-start"
              flex
              style={{
                maxWidth: tooltip ? 'calc(100% - 24px)' : null
              }}
            >
              <Text
                noWrap
                color={error ? 'error' : inkSoft}
                fontWeight={600}
                fontSize="11px"
              >
                {title === '&nbsp;' ? <>&nbsp;</> : title}
                {required && (
                  <Text component="span" color={red} fontWeight={600}>
                    {' '}
                    *
                  </Text>
                )}
              </Text>
            </Item>
            {tooltip && (
              <Item
                flex
                style={{
                  alignItem: 'center'
                }}
              >
                <HelpTooltip title={tooltip} />
              </Item>
            )}
          </Row>
        )}
        {label && (
          <InputLabel htmlFor="outlined-adornment-password">{label}</InputLabel>
        )}
        <OutlinedInput
          disabled={disabled}
          type={type}
          multiline={multiline}
          rows={rows}
          fullWidth
          label={label}
          defaultValue={defaultValue}
          placeholder={placeholder}
          value={disabled ? value : valInput}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          endAdornment={
            unit && <InputAdornment position="end">{unit}</InputAdornment>
          }
          inputProps={{
            ...(format && { format: `${format}` }),
            country,
            ...inputPropsProp
          }}
          inputComponent={format ? FormatInput : 'input'}
          labelWidth={label && label.length * 8}
          sx={{
            marginTop: title ? '5px' : undefined,
            '& .MuiOutlinedInput-root': {
              backgroundColor: `${bgColor || 'transparent'} !important` // ← Forcé ici
            },
            '& .MuiInputBase-input': {
              backgroundColor: `${bgColor || 'transparent'} !important` // ← Et ici pour l'input direct
            }
          }}
        />
        {((error && errorText) || helperText) && (
          <FormHelperText id="helper-text">
            {errorText || helperText}
          </FormHelperText>
        )}
      </FormControl>
    </Fade>
  );
};

export default TextField;

TextField.defaultProps = {
  color: null,
  defaultValue: null,
  disabled: false,
  error: false,
  errorText: null,
  fontSize: '12px',
  format: null,
  helperText: null,
  label: null,
  placeholder: null,
  onBlur: () => null,
  onChange: () => null,
  onEnter: () => null,
  show: null,
  small: true,
  type: 'text',
  unit: null,
  value: '',
  multiline: false,
  rows: 1,
  title: null,
  required: false,
  cleanOnEnter: false,
  inputProps: null
};
TextField.propTypes = {
  color: PropTypes.string,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  fontSize: PropTypes.string,
  format: PropTypes.string,
  helperText: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  show: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['none'])]),
  small: PropTypes.bool,
  type: PropTypes.string,
  unit: PropTypes.string,
  value: PropTypes.string,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  title: PropTypes.string,
  required: PropTypes.bool,
  cleanOnEnter: PropTypes.bool,
  inputProps: PropTypes.objectOf(PropTypes.any)
};
